import { makeStyles, shorthands, tokens, typographyStyles } from "@fluentui/react-components";

export const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",

    height: "85vh",
    width: "100%",
    "@media (max-width: 499px)": {
      height: "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
      minHeight: "-webkit-fill-available",
    },
  },
  main: {
    display: "flex",
    flexDirection: "column",
    height: "90vh",
    whiteSpace: "nowrap",
    position: "relative",
    alignItems: "stretch",
    ...shorthands.padding("8px", "0px", "20px", "0px"), // Reduced top padding
    ...shorthands.gap("6px"),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.overflow("hidden"),
    "@media (max-width: 499px)": {
      height: "100%",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    },
    "@media (min-width: 500px)": {
      alignItems: "stretch",
      backgroundColor: tokens.colorNeutralBackground2,
      ...shorthands.padding("8px", "0px", "24px", "0px"), // Reduced top padding
    },
  },
  subMainContainer: {
    display: "flex",
    flexDirection: "column",
    "@media (max-width: 499px)": {
      minHeight: "min-content",
      paddingBottom: "0px",
    },
    "@media (min-width: 500px)": {
      ...shorthands.padding("24px"),
      ...shorthands.borderRadius("8px"),
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow2,
      height: "100vh",
      overflowY: "auto",
      scrollbarGutter: "stable",
      "::-webkit-scrollbar": {
        width: "0px",
        height: "0px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#888",
      },
      "&:hover": {
        "::-webkit-scrollbar": {
          width: "4px",
          height: "4px",
        },
      },
    },
    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
      maxWidth: "100%",
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      ...shorthands.margin("0px", "auto"),
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
      maxWidth: "100%",
    },
    "@media (min-width: 1400px)": {
      ...shorthands.margin("auto"),
      marginTop: "12px",
      width: "1040px",
    },
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "@media (min-width: 500px)": {
      display: "flex",
      flexDirection: "row",
      height: "auto",
      marginBottom: "20px",
      ...shorthands.gap("12px"),
    },
  },
  image: {
    maxHeight: "160px",
    maxWidth: "100%",
    "@media (max-width: 499px)": {
      width: "100%",
      objectFit: "contain",
    },
    "@media (min-width: 500px)": {
      width: "288px",
      height: "192px",
      ...shorthands.flex(1),
      objectFit: "cover",
      ...shorthands.borderRadius("8px"),
    },
  },
  details: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "stretch",
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (min-width: 500px)": {
      width: "100%",
      ...shorthands.overflow("auto"),
      ...shorthands.padding("0px", "12px", "0px", "12px"),
    },
  },
  padding: {
    ...shorthands.padding("16px", "16px", "0px", "16px"),
  },
  description: {
    "&text": typographyStyles.subtitle1,
    fontSize: "15px",
    color: tokens.colorNeutralForeground4,
    whiteSpace: "pre-line",
  },
  allergen: {
    paddingTop: "10px",
    color: tokens.colorPalettePurpleForeground2,
  },
  dietaryLabel: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  calories: {
    "&text": typographyStyles.subtitle1,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    paddingTop: "4px",
    paddingLeft: "4px",
    color: tokens.colorNeutralForeground4,
  },
  priceCaloriesContainer: {
    alignSelf: "stretch",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding("0px", "0px", "12px", "0px"),
    "@media (min-width: 500px)": {
      ...shorthands.padding("0px", "12px", "12px", "12px"),
    },
  },
  footer: {
    position: "fixed",
    left: 0,
    bottom: 0,
    width: "100%",
    textAlign: "center",
    ...shorthands.padding("16px", "8px", "16px", "8px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: "inset 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    "@media (max-width: 499px)": {
      paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
    },
  },
  button: {
    ...shorthands.borderRadius("4px"),
    backgroundColor: tokens.colorPalettePurpleForeground2,
    color: "aliceblue",
    display: "flex",
    width: "100%",
    ...shorthands.padding("8px", "16px"),
    marginBottom: "12px",
    justifyContent: "center",
    alignItems: "center",
  },
  optionGroupContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    ...shorthands.gap("0px"),
    "@media (max-width: 499px)": {
      paddingBottom: "env(safe-area-inset-bottom)",
    },
    "@media (min-width: 500px)": {
      overflowY: "auto",
      scrollbarGutter: "stable",
      "::-webkit-scrollbar": {
        width: "0px",
        height: "0px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#888",
      },
      "&:hover": {
        "::-webkit-scrollbar": {
          width: "4px",
          height: "4px",
        },
      },
    },
  },
  error: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    height: "100px",
    maxWidth: "100vw",
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.border("1px", "solid", tokens.colorPaletteRedBackground2),
    ...shorthands.padding("16px", "16px", "16px", "16px"),
  },
  errorRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    ...shorthands.gap("4px"),
  },
  errorMessage: {
    ...shorthands.overflow("hidden"),
    maxWidth: "260px",
  },
});
